import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Puff } from 'react-loader-spinner'
import { BiTrash, BiCalendarHeart } from 'react-icons/bi'
import { getHolidays, deleteHolidays } from '../../actions/holidays'
import Button from '../../components/Button'
import Table from '../../components/Table'
import Confirmation from '../../components/Confirmation/Confirmation'
import HolidaysModal from './Modal'
import TabSelectModal from '../../components/TabSelectModal/TabSelectModal'
import styles from './styles.module.scss'

const TABLE_HEADERS = [
  { key: 'name', label: 'Название' },
  { key: 'date', label: 'Дата', width: '120px' },
  { key: 'text', label: 'Примечание' },
  { key: 'timestamp', label: 'Перенос', width: '120px' },
  { key: 'actions', label: '', width: '60px' }
]

export default function Holidays() {
  const dispatch = useDispatch()
  const { holidaysList, loading } = useSelector(state => state.holidays)

  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Фильтрация по колонкам
  const [selectedItemsTab, setSelectedItemsTab] = useState([])
  const [tab, setTab] = useState(null)
  const [selectedTab, setSelectedTab] = useState(null)

  useEffect(() => {
    dispatch(getHolidays())
  }, [dispatch])

  const filteredList = useMemo(() => {
    if (!holidaysList) return []
    if (!selectedItemsTab?.length || !tab) return holidaysList
    return holidaysList.filter(el => selectedItemsTab.includes(el[tab]))
  }, [holidaysList, selectedItemsTab, tab])

  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => a.name?.localeCompare(b.name))
  }, [filteredList])

  const handleDelete = useCallback((item) => (e) => {
    e.stopPropagation()
    setConfirmation({ id: item.hid, name: item.name })
  }, [])

  const openModal = useCallback((item) => () => {
    setSelectedItem(item || 'add')
  }, [])

  const closeModal = useCallback(() => {
    setSelectedItem(null)
  }, [])

  const closeSelectedTab = useCallback(() => {
    setSelectedTab(null)
  }, [])

  const acceptConfirmation = useCallback(async () => {
    if (!confirmation) return
    setIsDeleting(true)
    try {
      await dispatch(deleteHolidays(confirmation.id))
    } finally {
      setConfirmation(null)
      setIsDeleting(false)
    }
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const getSelectTab = useCallback((tabKey) => () => {
    if (!holidaysList) return
    const uniqueValues = [...new Set(holidaysList.map(el => el[tabKey]))]
    setSelectedTab(uniqueValues)
    setTab(tabKey)
    setSelectedItemsTab([])
  }, [holidaysList])

  const isModalOpen = selectedItem !== null
  const isAddMode = selectedItem === 'add'

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <BiCalendarHeart size={64} className={styles.emptyIcon} />
      <p>Нет добавленных праздничных дней</p>
      <Button onClick={openModal()}>Добавить первый праздник</Button>
    </div>
  )

  const renderLoading = () => (
    <div className={styles.loadingState}>
      <Puff
        height={50}
        width={50}
        color="#3689FF"
        ariaLabel="Загрузка..."
      />
      <span>Загрузка праздничных дней...</span>
    </div>
  )

  const renderTable = () => (
    <div className={styles.tableContainer}>
      <Table className={styles.table}>
        <thead>
          <tr>
            {TABLE_HEADERS.map(({ key, label, width }) => (
              <th
                key={key}
                style={width ? { width } : undefined}
                onClick={key !== 'actions' ? getSelectTab(key) : undefined}
                className={key !== 'actions' ? styles.clickableHeader : ''}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedList.map((item) => (
            <tr
              key={item.hid}
              onClick={openModal(item)}
              className={styles.tableRow}
            >
              <td>
                <span className={styles.itemName}>{item.name}</span>
              </td>
              <td>{item.date}</td>
              <td>
                <span className={styles.itemDescription}>{item.text}</span>
              </td>
              <td>{item.timestamp}</td>
              <td>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={handleDelete(item)}
                  title={`Удалить праздник ${item.name}`}
                  aria-label={`Удалить праздник ${item.name}`}
                >
                  <BiTrash size={20} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className={styles.actions}>
        <Button onClick={openModal()}>Добавить</Button>
        <span className={styles.counter}>
          Всего: {sortedList.length}
        </span>
      </div>
    </div>
  )

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1>Праздничные дни</h1>
      </header>

      <main className={styles.content}>
        {loading && renderLoading()}
        {!loading && sortedList.length === 0 && renderEmptyState()}
        {!loading && sortedList.length > 0 && renderTable()}
      </main>

      <HolidaysModal
        open={isModalOpen}
        onClose={closeModal}
        data={isAddMode ? null : selectedItem}
        addMode={isAddMode}
      />

      {selectedTab && (
        <TabSelectModal
          closeModal={closeSelectedTab}
          data={selectedTab}
          selectedItemsTab={selectedItemsTab}
          setSelectedItemsTab={setSelectedItemsTab}
          height={230}
        />
      )}

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Вы уверены, что хотите удалить "${confirmation.name}"?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
          loading={isDeleting}
        />
      )}
    </div>
  )
}
